import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Skeleton,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Search, SearchX, Download, Eye } from 'lucide-react';
import {
  DAM_DOC_TYPES,
  DAM_DEPARTMENTS,
  DAM_EXTENSIONS,
  DAM_FOLDERS,
  type DamFile,
} from 'mocks/dam';
import { searchDamFiles, type DamSearchFilters } from 'services/demoApi/damApi';
import { downloadDamFile } from 'services/demoApi/fileDownload';
import DamPreviewDialog from 'components/DamPreviewDialog';
import { FileExtIcon, formatSizeKb, formatDamDate } from './fileIcons';

const COLS = '24px 2.2fr 1.2fr 1.2fr 90px 1.2fr 110px 72px';
const ALL = '__all__';

const filterFieldSx = {
  minWidth: 170,
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    bgcolor: '#fff',
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

export default function SearchTab() {
  const [query, setQuery] = useState('');
  const [docType, setDocType] = useState(ALL);
  const [department, setDepartment] = useState(ALL);
  const [ext, setExt] = useState(ALL);
  const [results, setResults] = useState<DamFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<DamFile | null>(null);

  const handleDownload = async (file: DamFile) => {
    setDownloadingId(file.id);
    try {
      await downloadDamFile(file);
    } catch {
      // EC-7: молча для поиска — основная обработка в BrowseTab
    } finally {
      setDownloadingId(null);
    }
  };

  const filters: DamSearchFilters = useMemo(
    () => ({
      query: query || undefined,
      docType: docType === ALL ? undefined : docType,
      department: department === ALL ? undefined : department,
      ext: ext === ALL ? undefined : ext,
    }),
    [query, docType, department, ext]
  );

  // Дебаунс-поиск: каждый ввод перезапускает таймер
  useEffect(() => {
    setIsLoading(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      searchDamFiles(filters).then((files) => {
        if (cancelled) return;
        setResults(files);
        setIsLoading(false);
      });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters]);

  const folderName = (folderId: string) =>
    DAM_FOLDERS.find((folder) => folder.id === folderId)?.name ?? '—';

  return (
    <Box>
      {/* Поиск + фильтры */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Поиск по названию документа..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ ...filterFieldSx, flex: 1, minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', mr: 1, color: 'var(--color-text-muted)' }}>
                <Search size={15} />
              </Box>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Тип документа"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          sx={filterFieldSx}
        >
          <MenuItem value={ALL} sx={{ fontSize: 13 }}>
            Все типы
          </MenuItem>
          {DAM_DOC_TYPES.map((t) => (
            <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Подразделение"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          sx={filterFieldSx}
        >
          <MenuItem value={ALL} sx={{ fontSize: 13 }}>
            Все подразделения
          </MenuItem>
          {DAM_DEPARTMENTS.map((d) => (
            <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>
              {d}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Формат"
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          sx={{ ...filterFieldSx, minWidth: 120 }}
        >
          <MenuItem value={ALL} sx={{ fontSize: 13 }}>
            Все
          </MenuItem>
          {DAM_EXTENSIONS.map((x) => (
            <MenuItem key={x} value={x} sx={{ fontSize: 13 }}>
              .{x}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Счётчик */}
      <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 1.5 }}>
        {isLoading ? 'Поиск по хранилищу…' : `Найдено документов: ${results.length}`}
      </Typography>

      {/* Таблица результатов */}
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid var(--color-border)',
          boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 1.5,
            px: 2.5,
            py: 1,
            bgcolor: 'var(--color-surface-subtle)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {['', 'Название', 'Папка', 'Тип', 'Размер', 'Автор', 'Изменён', ''].map((h, i) => (
            <Typography
              key={i}
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              {Array.from({ length: 8 }).map((__, j) => (
                <Skeleton key={j} height={18} />
              ))}
            </Box>
          ))}

        {!isLoading && results.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <SearchX size={28} style={{ opacity: 0.5 }} />
            <Typography sx={{ fontSize: 13, mt: 1 }}>Ничего не найдено</Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.8 }}>
              Измените запрос или сбросьте фильтры
            </Typography>
          </Box>
        )}

        {!isLoading &&
          results.map((file) => (
            <Box
              key={file.id}
              onClick={() => setPreviewFile(file)}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                alignItems: 'center',
                borderBottom: '1px solid var(--color-border)',
                transition: 'background 0.12s',
                cursor: 'pointer',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'rgba(20,116,255,0.03)' },
              }}
            >
              <FileExtIcon ext={file.ext} />
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}
                noWrap
              >
                {file.name}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }} noWrap>
                {folderName(file.folderId)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }} noWrap>
                {file.docType}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                {formatSizeKb(file.sizeKb)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }} noWrap>
                {file.author}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                {formatDamDate(file.updatedAt)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton
                  size="small"
                  aria-label={`Просмотреть ${file.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewFile(file);
                  }}
                  sx={{
                    color: 'var(--color-text-muted)',
                    '&:hover': { color: 'var(--color-primary)' },
                  }}
                >
                  <Eye size={14} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Скачать ${file.name}`}
                  disabled={downloadingId === file.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDownload(file);
                  }}
                  sx={{
                    color: 'var(--color-text-muted)',
                    '&:hover': { color: 'var(--color-primary)' },
                  }}
                >
                  {downloadingId === file.id ? (
                    <CircularProgress size={13} />
                  ) : (
                    <Download size={14} />
                  )}
                </IconButton>
              </Box>
            </Box>
          ))}
      </Box>

      <DamPreviewDialog
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}
